const adthosDbName = 'AdthosLocalDb';

import config from 'app-core/config';
import storage from 'storage';
import moment from 'moment';
import _ from 'lodash';

function registerEvents() {
	config.eventsBus.$on('login', () => {
		if (__DEBUG__)
			console.debug('Local db on login');
		config.creativeBlobsDb.cacheEnabled = storage.prop('creativeBlobsDb_cacheEnabled', void 0, true).value;
	});
}

function dbValue(key, value, storeName, customOperation, customInfo) {
	storeName = storeName || 'GlobalStore';
	return new Promise(res => {
		try {
			let processResult = (db) => {
				var tx = db.transaction(storeName, 'readwrite');
				var store = tx.objectStore(storeName);

				if (value !== void 0 && key) {
					store.put({ id: key, value, timestamp: new Date(), ...(customInfo || {}) });
				}

				var result = store.get(key);
				const processResult = () => {
					if (customOperation) {
						customOperation(store, result?.result?.value);
					}
					res(result?.result?.value);
				};
				result.onsuccess = processResult;
				result.onerror = processResult;
				tx.onerror = processResult;

				tx.oncomplete = function () {
					db.close();
				};
			}

			const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB || window.shimIndexedDB;
			const dbOpen = indexedDB.open(`${adthosDbName}_${storeName}`);
			dbOpen.onupgradeneeded = () => {
				var db = dbOpen.result;
				db.createObjectStore(storeName, { keyPath: 'id' });
			}
			dbOpen.onsuccess = function () {
				var db = dbOpen.result;
				processResult(db);
			}
			dbOpen.onerror = () => res(null);
		} catch {
			res(null);
		}
	});
}

function storeOperation(storeName, operation) {
	storeName = storeName || 'GlobalStore';
	return new Promise(res => {
		const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB || window.shimIndexedDB;
		const dbOpen = indexedDB.open(`${adthosDbName}_${storeName}`);
		dbOpen.onupgradeneeded = () => {
			var db = dbOpen.result;
			db.createObjectStore(storeName, { keyPath: 'id' });
		}
		dbOpen.onsuccess = async function () {
			var db = dbOpen.result;
			var tx = db.transaction(storeName, 'readwrite');
			var store = tx.objectStore(storeName);

			if (operation) {
				return res(await operation(store));
			}

			tx.oncomplete = function () {
				db.close();
			};
			res();
		}
		dbOpen.onerror = () => res();
	});
}

function clearDb(storeName) {
	storeName = storeName || 'GlobalStore';
	const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB || window.shimIndexedDB;
	const dbOpen = indexedDB.open(`${adthosDbName}_${storeName}`);
	dbOpen.onsuccess = function () {
		var db = dbOpen.result;
		var tx = db.transaction(storeName, 'readwrite');
		var store = tx.objectStore(storeName);
		store.clear();
	};
}

function initStore(storeName) {
	const store = {
		cacheEnabled: true,
		get: async (key, func, customInfo) => {
			try {
				if (!store.cacheEnabled) return null;
				let start = new Date();
				let value = await dbValue(key, void 0, storeName);
				if (value === void 0 && func) {
					value = await func();
					await dbValue(key, value, storeName, void 0, customInfo);
				}
				if (__DEBUG__)
					console.debug('Local db get', { key, func, storeName, time: moment().diff(start, 'miliseconds') });
				return value;
			} catch {
				return null;
			}
		},
		set: async (key, value, customInfo) => {
			try {
				if (!store.cacheEnabled) return null;
				if (__DEBUG__)
					console.debug('Local db set', { key, value, storeName });
				return dbValue(key, value, storeName, void 0, customInfo);
			} catch {
				return null;
			}
		},
		delete: async (keys) => {
			try {
				keys = Array.isArray(keys) ? keys : [keys];
				storeOperation(storeName, async (store) => {
					keys.map(f => store.delete(f));
				});
			} catch {
				return null;
			}
		},
		all: async (query, count) => {
			return new Promise((res) => {
				storeOperation(storeName, async (store) => {
					const req = store.getAll(query, count);
					req.onsuccess = () => res(req?.result);
					req.onerror = () => res(req?.result);
				});
			});
		},
		search: async (predicate) => {
			return new Promise((res) => {
				storeOperation(storeName, async (store) => {
					const result = [];
					var request = store.openCursor();
					request.onsuccess = function(event) {
						var cursor = event.target.result;
						if (cursor) {
							if (predicate && predicate(cursor.value)) {
								result.push(cursor.value);
							}

							cursor.continue();          
						} else {
							res(result);
						}
					};
				});
			});
			
		},
		clearStore: () => {
			clearDb(storeName)
		},
		getStatistics: (items) => {
			let maxDate = null;
			let totalBytes = 0;
			let blobsCount = 0;

			items.map(f => {
				if (f?.value instanceof Blob) {
					totalBytes += f?.value?.size || 0;
					blobsCount++;
					maxDate = (!maxDate || (maxDate < f.timestamp)) ? f.timestamp : maxDate;
				}
			});

			return {
				maxDate,
				totalBytes,
				blobsCount
			}
		}
	}

	return store;
}

export default {
	initStore,
	dbValue,
	clearDb,
	registerEvents
}